import type { GetterTree } from 'vuex'
import { utils } from '@fusion/saas-components-v3'
import type { RootState } from '@/store'
import type { DevicesState, LatestStudy } from './types'
import type { DeviceCoords, FormattedMeasurement } from '@/services/device-service/devices'
import type { AveragePM, ChangeOverTime, DeviceActivations } from '@/types/devices'
import type { UserCalibration } from '@/services/device-calibration'
import type { TelemetryReading } from '@/helpers/devices/telemetryHelper'
import type { StatisticsReading } from '@/helpers/studies/statisticsHelper'
import { hasLatestStudyErrorState } from './helper'
import { DEVICE_GETTERS } from './types'

const terminationReasons = utils.constants.study.terminationReasons

type DeviceGetterReturns = Record<
  keyof IDeviceGetters,
  ReturnType<IDeviceGetters[keyof IDeviceGetters]>
>

interface IDeviceGetters extends GetterTree<DevicesState, RootState> {
  [DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => string
  [DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => DeviceCoords
  [DEVICE_GETTERS.GET_SENSOR_READINGS_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => Record<string, FormattedMeasurement>
  [DEVICE_GETTERS.GET_DEVICE_STATUS_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string[]
  [DEVICE_GETTERS.GET_SENSOR_STATUS_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string[]
  [DEVICE_GETTERS.GET_RSSI_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => number | null
  [DEVICE_GETTERS.GET_CONNECTION_TYPE_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_SPS_EOL_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => boolean | null
  [DEVICE_GETTERS.GET_USER_CALS_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => UserCalibration[]
  [DEVICE_GETTERS.GET_FIRMWARE_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_MQTT_HOST_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_AVERAGES_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => AveragePM
  [DEVICE_GETTERS.GET_PERCENT_CHANGED_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => ChangeOverTime
  [DEVICE_GETTERS.GET_IS_PUBLIC_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_PRIVACY_ZONE_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => unknown
  [DEVICE_GETTERS.GET_LAST_CONTACT_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => string
  [DEVICE_GETTERS.GET_LAST_COMMUNICATION_TIME_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_LAST_TELEMETRY_TIMESTAMP_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_LAST_TELEMETRY_STUDY_ID_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_IS_INDOOR_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_ZERO_APPLIED_AT_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => Record<string, string> | null
  [DEVICE_GETTERS.GET_ZERO_STATE_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => Record<string, string> | null
  [DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => string
  [DEVICE_GETTERS.GET_LOCATION_NAME_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => string
  [DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => string
  [DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => string
  [DEVICE_GETTERS.GET_DEVICE_TYPE_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => string
  [DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => string
  [DEVICE_GETTERS.GET_MEASUREMENTS_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => Record<string, number | boolean | null>
  [DEVICE_GETTERS.GET_SUBSCRIPTION_METADATA_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => DeviceActivations
  [DEVICE_GETTERS.GET_SUBSCRIPTION_TYPE_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string
  [DEVICE_GETTERS.GET_WEBSITE_SUBSCRIPTION_NAME](
    state: DevicesState
  ): (deviceId: string) => string | undefined
  [DEVICE_GETTERS.GET_WEBSITE_SUBSCRIPTION_SUB_TYPE](
    state: DevicesState
  ): (deviceId: string) => string | undefined
  [DEVICE_GETTERS.GET_PERMISSIONS_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => string[]
  [DEVICE_GETTERS.GET_DEVICE_ID_BY_DEVICE_SERIAL](
    state: DevicesState
  ): (serial: string) => string | undefined
  [DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_MODEL_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string
  [DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_SERIAL_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_ID_BY_DEVICE_ID](
    state: DevicesState,
    getters: DeviceGetterReturns
  ): (deviceId: string) => string
  [DEVICE_GETTERS.GET_SMART_BRIDGE_CONNECTED_DEVICE_IDS_BY_DEVICE_ID](
    state: DevicesState,
    getters: DeviceGetterReturns
  ): (deviceId: string) => string[]
  [DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID](state: DevicesState): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_IS_TELEMETRY_24HRS_ACTIVE_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_TELEM_TX_TYPE_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => number | null
  [DEVICE_GETTERS.GET_IS_OFFLINE_PRESENT_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_IS_AUTO_UPLOAD_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_IS_LIVE_PRESENT_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_IS_BATTERY_CHARGING_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_IS_BATTERY_INSTALLED_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_IS_BATTERY_LOW_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_BATTERY_REMAINING_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => number | null
  [DEVICE_GETTERS.GET_CALIBRATION_DATE_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_FLOW_CALIBRATION_FACTOR_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => number | null
  [DEVICE_GETTERS.GET_CALIBRATION_NAME_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_PHOTOMETRIC_CAL_FACTOR_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => number | null
  [DEVICE_GETTERS.GET_SIZE_CAL_FACTOR_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => number | null
  [DEVICE_GETTERS.GET_ERROR_STATE_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string[] | null
  [DEVICE_GETTERS.GET_IS_THERE_A_VALUE](
    state: DevicesState
  ): (deviceId: string, key: string) => boolean
  [DEVICE_GETTERS.GET_DEVICE_ALARMS_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string[] | null
  [DEVICE_GETTERS.GET_DEVICES_WITH_CONNECTED_AFTER_START_ERROR](state: DevicesState): string[]
  [DEVICE_GETTERS.GET_DEVICES_WITH_INCOMPLETE_DATA_ERROR](state: DevicesState): string[]
  [DEVICE_GETTERS.GET_ALL_DEVICES_STUDY_ID](state: DevicesState): Record<string, string>
  [DEVICE_GETTERS.IS_DEVICE_CONNECTED_TO_SMART_BRIDGE](
    state: DevicesState,
    getters: DeviceGetterReturns
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.HAS_LATEST_STUDY_BY_DEVICE_ID](
    state: DevicesState,
    getters: DeviceGetterReturns
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_LATEST_STUDY_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => LatestStudy | null
  [DEVICE_GETTERS.GET_LATEST_STUDY_MIN_TIMESTAMP_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_LATEST_STUDY_MAX_TIMESTAMP_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
  [DEVICE_GETTERS.GET_LATEST_STUDY_DURATION_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => number | null
  [DEVICE_GETTERS.GET_LATEST_STUDY_MEASUREMENT_STATISTICS_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => Record<string, TelemetryReading | StatisticsReading> | null
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_CONNECTED_AFTER_START](
    state: DevicesState,
    getters: DeviceGetterReturns
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_COMPLETE](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_INCOMPLETE](
    state: DevicesState,
    getters: DeviceGetterReturns
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_IN_PROGRESS](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_PENDING](
    state: DevicesState
  ): (deviceId: string) => boolean
  [DEVICE_GETTERS.GET_LATEST_STUDY_SAMPLING_INTERVAL_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => number | null
  [DEVICE_GETTERS.GET_LATEST_STUDY_START_TIME_BY_DEVICE_ID](
    state: DevicesState
  ): (deviceId: string) => string | null
}

const getters: IDeviceGetters = {
  [DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.accountId[deviceId]
  },
  [DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.coords[deviceId]
  },
  [DEVICE_GETTERS.GET_SENSOR_READINGS_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.sensorReadings[deviceId]
  },
  [DEVICE_GETTERS.GET_DEVICE_STATUS_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.deviceStatus[deviceId] || []
  },
  [DEVICE_GETTERS.GET_SENSOR_STATUS_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.sensorStatus[deviceId] || []
  },
  [DEVICE_GETTERS.GET_RSSI_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.rssi[deviceId]
  },
  [DEVICE_GETTERS.GET_CONNECTION_TYPE_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.connectionType[deviceId]
  },
  [DEVICE_GETTERS.GET_SPS_EOL_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.sensorEoL[deviceId]
  },
  [DEVICE_GETTERS.GET_USER_CALS_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.userCals[deviceId]
  },
  [DEVICE_GETTERS.GET_FIRMWARE_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.firmware[deviceId]
  },
  [DEVICE_GETTERS.GET_MQTT_HOST_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.mqttHost[deviceId] || null
  },
  [DEVICE_GETTERS.GET_AVERAGES_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.averages[deviceId]
  },
  [DEVICE_GETTERS.GET_PERCENT_CHANGED_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.percentChanged[deviceId]
  },
  [DEVICE_GETTERS.GET_IS_PUBLIC_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.public[deviceId]
  },
  [DEVICE_GETTERS.GET_PRIVACY_ZONE_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.privacyZone[deviceId]
  },
  [DEVICE_GETTERS.GET_LAST_CONTACT_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.lastTelemetry[deviceId]
  },
  [DEVICE_GETTERS.GET_LAST_COMMUNICATION_TIME_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.lastCommunicationTime[deviceId] || null
  },
  [DEVICE_GETTERS.GET_LAST_TELEMETRY_TIMESTAMP_BY_DEVICE_ID]: (state) => (deviceId) => {
    if (state.lastTelemetry[deviceId]) {
      return state.lastTelemetry[deviceId]
    }
    return null
  },
  [DEVICE_GETTERS.GET_LAST_TELEMETRY_STUDY_ID_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.lastTelemetryStudyId[deviceId] || null
  },
  [DEVICE_GETTERS.GET_IS_INDOOR_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.indoor[deviceId]
  },
  [DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.shared[deviceId]
  },
  [DEVICE_GETTERS.GET_ZERO_APPLIED_AT_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.zeroAppliedAt[deviceId]
  },
  [DEVICE_GETTERS.GET_ZERO_STATE_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.zeroState[deviceId]
  },
  [DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.name[deviceId]
  },
  [DEVICE_GETTERS.GET_LOCATION_NAME_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.locationName[deviceId]
  },
  [DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.model[deviceId]
  },
  [DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.submodel[deviceId]
  },
  [DEVICE_GETTERS.GET_DEVICE_TYPE_BY_DEVICE_ID]: (state) => (deviceId) => {
    const model = state.model[deviceId]
    const submodel = state.submodel[deviceId]
    // TODO: Submodel should come from some field in the firestore device model metadata for the model's submodel, see https://jira.tsi.com/browse/FTD-2127
    return submodel ? `${model}-0${submodel[0]}` : model
  },
  [DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.serial[deviceId]
  },
  [DEVICE_GETTERS.GET_MEASUREMENTS_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.measurements[deviceId]
  },
  [DEVICE_GETTERS.GET_SUBSCRIPTION_METADATA_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.subscriptionMetadata[deviceId]
  },
  [DEVICE_GETTERS.GET_SUBSCRIPTION_TYPE_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.subscriptionType[deviceId]
  },
  [DEVICE_GETTERS.GET_WEBSITE_SUBSCRIPTION_NAME]: (state) => (deviceId) => {
    return state.subscriptionMetadata[deviceId]?.website?.name
  },
  [DEVICE_GETTERS.GET_WEBSITE_SUBSCRIPTION_SUB_TYPE]: (state) => (deviceId) => {
    const websiteSubscription = state.subscriptionMetadata[deviceId]?.website
    return websiteSubscription?.subType
  },
  [DEVICE_GETTERS.GET_PERMISSIONS_BY_DEVICE_ID]: (state) => (deviceId) => {
    if (state.subscriptionMetadata?.[deviceId]) {
      const metadata = state.subscriptionMetadata[deviceId]
      if (metadata.permissions && Array.isArray(metadata.permissions)) {
        return metadata.permissions
      }
    }
    return []
  },
  [DEVICE_GETTERS.GET_DEVICE_ID_BY_DEVICE_SERIAL]: (state) => (serial) => {
    return serial ? Object.keys(state.serial).find((key) => state.serial[key] === serial) : ''
  },
  [DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_MODEL_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.connectedSmartBridgeModel[deviceId]
  },
  [DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_SERIAL_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.connectedSmartBridgeSerial[deviceId]
  },
  [DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_ID_BY_DEVICE_ID]: (_state, getters) => (deviceId) => {
    const smartBridgeSerial =
      getters[DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_SERIAL_BY_DEVICE_ID](deviceId) ?? ''
    return getters[DEVICE_GETTERS.GET_DEVICE_ID_BY_DEVICE_SERIAL](smartBridgeSerial) ?? ''
  },
  [DEVICE_GETTERS.GET_SMART_BRIDGE_CONNECTED_DEVICE_IDS_BY_DEVICE_ID]:
    (state, getters) => (deviceId) => {
      const smartBridgeSerial = getters[DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID](deviceId)
      if (smartBridgeSerial) {
        return Object.keys(state.connectedSmartBridgeSerial).filter(
          (key) => state.connectedSmartBridgeSerial[key] === smartBridgeSerial
        )
      }
      return []
    },
  [DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID]: (state) => (deviceId) => {
    return !!state.isConnected[deviceId]
  },
  [DEVICE_GETTERS.GET_IS_TELEMETRY_24HRS_ACTIVE_BY_DEVICE_ID]: (state) => (deviceId) => {
    return !!state.isTelemetry24HrsActive[deviceId]
  },
  [DEVICE_GETTERS.GET_TELEM_TX_TYPE_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.telemTxType[deviceId]
  },
  [DEVICE_GETTERS.GET_IS_OFFLINE_PRESENT_BY_DEVICE_ID]: (state) => (deviceId) => {
    return !!state.uploadStatus[deviceId]?.offline_present
  },
  [DEVICE_GETTERS.GET_IS_AUTO_UPLOAD_BY_DEVICE_ID]: (state) => (deviceId) => {
    return !!state.uploadStatus[deviceId]?.auto_upload
  },
  [DEVICE_GETTERS.GET_IS_LIVE_PRESENT_BY_DEVICE_ID]: (state) => (deviceId) => {
    return !!state.uploadStatus[deviceId]?.live_present
  },
  [DEVICE_GETTERS.GET_IS_BATTERY_CHARGING_BY_DEVICE_ID]: (state) => (deviceId) => {
    return !!state.batteryCharging[deviceId]
  },
  [DEVICE_GETTERS.GET_IS_BATTERY_INSTALLED_BY_DEVICE_ID]: (state) => (deviceId) => {
    return !!state.batteryInstalled[deviceId]
  },
  [DEVICE_GETTERS.GET_IS_BATTERY_LOW_BY_DEVICE_ID]: (state) => (deviceId) => {
    return !!state.batteryLow[deviceId]
  },
  [DEVICE_GETTERS.GET_BATTERY_REMAINING_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.batteryRemaining[deviceId]
  },
  [DEVICE_GETTERS.GET_CALIBRATION_DATE_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.calibrationDate[deviceId] || null
  },
  [DEVICE_GETTERS.GET_FLOW_CALIBRATION_FACTOR_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.flowCalibrationFactor[deviceId]
  },
  [DEVICE_GETTERS.GET_CALIBRATION_NAME_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.userCal[deviceId]?.name ?? null
  },
  [DEVICE_GETTERS.GET_PHOTOMETRIC_CAL_FACTOR_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.userCal[deviceId]?.photometric_cal_factor ?? null
  },
  [DEVICE_GETTERS.GET_SIZE_CAL_FACTOR_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.userCal[deviceId]?.size_cal_factor ?? null
  },
  [DEVICE_GETTERS.GET_ERROR_STATE_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.errorState[deviceId]
  },
  // getIsThereAValue determines if a value is different from null and undefined
  [DEVICE_GETTERS.GET_IS_THERE_A_VALUE]: (state) => (deviceId, key) => {
    const indexableState = state as unknown as Record<string, Record<string, unknown>>
    if (Object.hasOwn(indexableState, key)) {
      const value = indexableState[key][deviceId]
      return value !== null && value !== undefined
    }
    return false
  },
  [DEVICE_GETTERS.GET_DEVICE_ALARMS_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.alarms[deviceId]
  },
  [DEVICE_GETTERS.GET_DEVICES_WITH_CONNECTED_AFTER_START_ERROR]: (state) => {
    if (!state.errorState) {
      return []
    }
    return Object.keys(state.errorState).filter((deviceId) => {
      return state.errorState[deviceId]?.includes(
        terminationReasons.CONNECTED_AFTER_START.toUpperCase()
      )
    })
  },
  [DEVICE_GETTERS.GET_DEVICES_WITH_INCOMPLETE_DATA_ERROR]: (state) => {
    if (!state.errorState) {
      return []
    }
    return Object.keys(state.errorState).filter((deviceId) => {
      return state.errorState[deviceId]?.includes(terminationReasons.INCOMPLETE_DATA.toUpperCase())
    })
  },
  [DEVICE_GETTERS.GET_ALL_DEVICES_STUDY_ID]: (state) => {
    if (!state.lastTelemetryStudyId) {
      return {}
    }
    return Object.keys(state.lastTelemetryStudyId).reduce(
      (acc: Record<string, string>, deviceId) => {
        const studyId = state.lastTelemetryStudyId[deviceId]
        if (studyId) {
          acc[deviceId] = studyId
        }
        return acc
      },
      {}
    )
  },
  [DEVICE_GETTERS.IS_DEVICE_CONNECTED_TO_SMART_BRIDGE]: (_state, getters) => (deviceId) => {
    // Just check if the device has a connected smart bridge serial.
    // We don't need to look for the SmartBridge's deviceId here because
    // if the SmartBridge is disconnected it won't have a deviceId in the store
    return !!getters[DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_SERIAL_BY_DEVICE_ID](deviceId)
  },
  // study getters. Consider making getters a folder with different methods for studies, cloud state, device state, etc.
  // OR split studies into its own store module
  [DEVICE_GETTERS.HAS_LATEST_STUDY_BY_DEVICE_ID]: (_state, getters) => (deviceId) => {
    return getters[DEVICE_GETTERS.GET_IS_THERE_A_VALUE](deviceId, 'latestStudy')
  },
  [DEVICE_GETTERS.GET_LATEST_STUDY_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.latestStudy[deviceId] || null
  },
  [DEVICE_GETTERS.GET_LATEST_STUDY_MIN_TIMESTAMP_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.latestStudy[deviceId]?.minTimestamp ?? null
  },
  [DEVICE_GETTERS.GET_LATEST_STUDY_MAX_TIMESTAMP_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.latestStudy[deviceId]?.maxTimestamp ?? null
  },
  [DEVICE_GETTERS.GET_LATEST_STUDY_DURATION_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.latestStudy[deviceId]?.duration ?? null
  },
  [DEVICE_GETTERS.GET_LATEST_STUDY_MEASUREMENT_STATISTICS_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.latestStudy[deviceId]?.measurementStatistics ?? null
  },
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_CONNECTED_AFTER_START]:
    (_state, getters) => (deviceId) => {
      const latestErrorState = getters[DEVICE_GETTERS.GET_ERROR_STATE_BY_DEVICE_ID](deviceId)
      const errorState = terminationReasons.CONNECTED_AFTER_START.toUpperCase()
      return hasLatestStudyErrorState(errorState, latestErrorState)
    },
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_COMPLETE]: (state) => (deviceId) => {
    const latestStudy = state.latestStudy[deviceId]
    if (latestStudy.uploadComplete) {
      return latestStudy.terminationReason === terminationReasons.COMPLETE
    }
    return false
  },
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_INCOMPLETE]: (_state, getters) => (deviceId) => {
    const latestErrorState = getters[DEVICE_GETTERS.GET_ERROR_STATE_BY_DEVICE_ID](deviceId)
    const errorState = terminationReasons.INCOMPLETE_DATA.toUpperCase()
    return hasLatestStudyErrorState(errorState, latestErrorState)
  },
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_IN_PROGRESS]: (state) => (deviceId) => {
    const latestStudy = state.latestStudy[deviceId]
    const terminationReason = latestStudy.terminationReason
    return terminationReason === 'Pending' || terminationReason === terminationReasons.IN_PROGRESS
  },
  [DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_PENDING]: (state) => (deviceId) => {
    const latestStudy = state.latestStudy[deviceId]
    const terminationReason = latestStudy.terminationReason
    return terminationReason === 'Pending'
  },
  [DEVICE_GETTERS.GET_LATEST_STUDY_SAMPLING_INTERVAL_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.latestStudy[deviceId]?.samplingInterval ?? null
  },
  [DEVICE_GETTERS.GET_LATEST_STUDY_START_TIME_BY_DEVICE_ID]: (state) => (deviceId) => {
    return state.latestStudy[deviceId]?.startTime ?? null
  }
}

export default getters
